import React, {useLayoutEffect, useState} from 'react';
import {SafeAreaView, View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';

import sy from '~/styles';
import {
  PrimaryButton,
  FormTextInput,
  Text,
  LinearProgress,
  CloseButton,
} from '~/components/controls';
import {
  addJobSetInitials,
  addJobSetFirstName,
  addJobSetLastName,
  addJobSetEmail,
  addJobSetPhone,
} from '~/actions';
import {useDispatch, useSelector} from '~/lib/hooks';
import PublicContentView from '~/components/shared/PublicContentView';

import BadgeIcon from '~/images/md-icons/badge/materialicons/24px.svg';
import ContactMailIcon from '~/images/md-icons/contact_mail/materialicons/24px.svg';
import ContactPhoneIcon from '~/images/md-icons/contact_phone/materialicons/24px.svg';
import ShoppingCartIcon from '~/images/md-icons/shopping_cart/materialicons/24px.svg';

/* eslint-disable no-control-regex */
const emailRegex =
  /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi;
const phoneRegex = /^06[0-9]{8}/gi;

const Index = ({navigation, route, onDismiss}) => {
  const dealer_id = route?.params?.dealer_id;
  const is_change = route?.params?.is_change;

  const dispatch = useDispatch();
  const stored = useSelector((state) => ({
    initials: state.initials,
    firstName: state.firstName,
    lastName: state.lastName,
    phone_number: state.phone_number,
    email: state.email,
  }));

  const is_company_car = useSelector((state) => state.is_company_car);

  const [initials, setInitials] = useState(stored.initials);
  const [firstName, setFirstName] = useState(stored.firstName);
  const [lastName, setLastName] = useState(stored.lastName);
  const [email, setEmail] = useState(stored.email);
  const [phone_number, setPhoneNumber] = useState(stored.phone_number);

  const [emailError, setEmailError] = useState(
    email && !email?.match(emailRegex),
  );
  const [phoneError, setPhoneError] = useState(
    phone_number && !phone_number?.match(phoneRegex),
  );

  useLayoutEffect(() => {
    navigation?.setOptions({
      headerShown: true,
      headerStatusBarHeight: 0,
      headerTitleAlign: 'center',
      headerTitle: () => (
        <LinearProgress style={{width: 126}} step={2} total={7} />
      ),
    });
  }, [navigation]);

  const isValid =
    email?.match(emailRegex) &&
    phone_number?.match(phoneRegex) &&
    !!initials &&
    !!lastName;

  const onNext = () => {
    if (!isValid) {
      return;
    }

    dispatch(addJobSetInitials(initials));
    dispatch(addJobSetFirstName(firstName));
    dispatch(addJobSetLastName(lastName));
    dispatch(addJobSetPhone(phone_number));
    dispatch(addJobSetEmail(email));

    if (is_change) {
      navigation.replace('Confirm', {dealer_id});
    } else if (onDismiss) {
      onDismiss();
    } else {
      navigation.navigate('Type', {dealer_id});
    }
  };

  const content = (
    <>
      {!onDismiss ? (
        <>
          <Divider />
          <View style={[sy['p-4']]}>
            <Text style={sy.xxLarge}>Jouw gegevens</Text>
            <Text style={[sy.large, sy['mt-4']]}>
              Je naam en hoe kunnen we je bereiken over de opdracht?
            </Text>
          </View>
          <Divider />
        </>
      ) : (
        <View style={sy.modalHeaderView}>
          <CloseButton
            style={{position: 'absolute', top: 8, left: 8}}
            onPress={onDismiss}
          />
          <Text style={sy.medium}>Contact</Text>
        </View>
      )}
      <View style={[sy.contentView, sy['p-4'], sy['gap-4']]}>
        <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
          <BadgeIcon fill="#4a4a49" />
          <FormTextInput
            style={[sy['flex-1']]}
            value={initials || ''}
            onChangeText={(text) => setInitials(text)}
            required={true}
            errorMessage={'Voorletter(s) is verplicht'}
            label="Voorletter(s)"
          />
        </View>
        <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
          <FormTextInput
            style={[sy['ml-14'], sy['flex-1']]}
            value={firstName || ''}
            onChangeText={(text) => setFirstName(text)}
            label="Voornaam"
          />
        </View>

        <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
          <FormTextInput
            style={[sy['ml-14'], sy['flex-1']]}
            value={lastName || ''}
            onChangeText={(text) => setLastName(text)}
            required={true}
            errorMessage={'Achternaam is verplicht'}
            label="Achternaam"
          />
        </View>

        <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
          <ContactPhoneIcon fill="#4a4a49" />
          <FormTextInput
            style={[sy['flex-1']]}
            value={phone_number || ''}
            onChangeText={(value) => {
              setPhoneNumber(value);
              setPhoneError(false);
            }}
            onEndEditing={() => {
              const valid =
                !phone_number?.length && phone_number?.match(phoneRegex);
              setPhoneError(!valid);
            }}
            hasError={phoneError}
            required={true}
            errorMessage={
              !phone_number?.length
                ? 'Mobiele telefoonnummer is verplicht'
                : !phone_number?.match(phoneRegex)
                ? 'Ongeldige waarde mobiele telefoonnummer'
                : null
            }
            digits={0}
            label="Mobiele telefoonnummer"
            inputProps={{
              maxLength: 10,
              keyboardtype: 'numeric',
            }}
          />
        </View>
        <View style={[sy['flex-row'], sy['gap-8'], sy['items-center']]}>
          <ContactMailIcon fill="#4a4a49" />
          <FormTextInput
            style={[sy['flex-1']]}
            value={email || ''}
            onChangeText={(value) => {
              setEmail(value);
              setEmailError(false);
            }}
            onEndEditing={() => {
              const valid = email?.length && email?.match(emailRegex);
              setEmailError(!valid);
            }}
            hasError={emailError}
            required={true}
            errorMessage={
              !email?.length
                ? 'Email verplicht'
                : !email?.match(emailRegex)
                ? 'Email heeft een ongeldige waarde'
                : null
            }
            label="Emailadres"
            inputProps={{
              keyboardtype: 'email-address',
            }}
          />
        </View>
      </View>
    </>
  );

  const footer = (
    <View style={sy.footerView}>
      <PrimaryButton
        disabled={!isValid}
        onPress={onNext}
        icon={
          is_change &&
          (() => <ShoppingCartIcon fill="#ffffff" width={18} height={18} />)
        }>
        {is_change ? 'Controleren' : onDismiss ? 'Opslaan' : 'Volgende'}
      </PrimaryButton>
    </View>
  );

  return (
    <SafeAreaView style={sy.mainView}>
      {onDismiss ? (
        <>
          <ScrollView>{content}</ScrollView>
          <View style={{borderTopWidth: 1, borderTopColor: '#dcdcdc'}}>
            {footer}
          </View>
        </>
      ) : (
        <PublicContentView>
          {{
            content,
            footer,
          }}
        </PublicContentView>
      )}
    </SafeAreaView>
  );
};

export default Index;
